import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaBuilding, FaCheck, FaChevronDown } from 'react-icons/fa'
import { Membership } from 'src/services/user'
import { cn } from 'src/utils/style'

interface OrgPickerProps {
  memberships: Membership[]
  value: string | null
  onChange: (organizationId: string) => void
  className?: string
  /** Shown when `value` matches no membership. Without it, the first org is shown. */
  placeholder?: string
}

/**
 * Controlled organisation picker for the one place org actually matters: the
 * wallet (billing) and billing-triggering actions. Deliberately *not* a global
 * context switch — the app's list pages aggregate across all of a user's orgs,
 * so scoping is local to wherever a single org must be chosen.
 *
 * A single-org user gets a static label (nothing to pick); a user with no
 * memberships renders nothing. The dropdown is a proper `listbox`/`option`
 * combobox with keyboard navigation rather than a bespoke grid of `<button>`s.
 */
export const OrgPicker = ({
  memberships,
  value,
  onChange,
  className,
  placeholder,
}: OrgPickerProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLLIElement | null)[]>([])

  const selected = useMemo(
    () =>
      memberships.find((m) => m.organizationId === value) ??
      (placeholder ? undefined : memberships[0]),
    [memberships, value, placeholder],
  )

  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  // When opening, focus the currently-selected option so arrow keys start there.
  useEffect(() => {
    if (!isOpen) return
    const selectedIndex = Math.max(
      memberships.findIndex((m) => m.organizationId === selected?.organizationId),
      0,
    )
    setActiveIndex(selectedIndex)
    optionRefs.current[selectedIndex]?.focus()
  }, [isOpen, memberships, selected])

  if (memberships.length === 0) return null

  if (memberships.length === 1 && selected) {
    return (
      <div
        className={cn('flex items-center gap-2 text-sm text-text-secondary', className)}
        title={selected.organizationName}
        data-testid="org-picker-single"
      >
        <FaBuilding className="h-4 w-4 shrink-0" />
        <span className="truncate">{selected.organizationName}</span>
      </div>
    )
  }

  const handleSelect = (membership: Membership) => {
    onChange(membership.organizationId)
    setIsOpen(false)
  }

  const moveActive = (delta: number) => {
    const next = (activeIndex + delta + memberships.length) % memberships.length
    setActiveIndex(next)
    optionRefs.current[next]?.focus()
  }

  const handleListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        moveActive(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        moveActive(-1)
        break
      case 'Home':
        e.preventDefault()
        setActiveIndex(0)
        optionRefs.current[0]?.focus()
        break
      case 'End':
        e.preventDefault()
        setActiveIndex(memberships.length - 1)
        optionRefs.current[memberships.length - 1]?.focus()
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        handleSelect(memberships[activeIndex])
        break
      case 'Escape':
      case 'Tab':
        setIsOpen(false)
        break
    }
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpen(true)
          }
        }}
        title={selected?.organizationName}
        aria-label={t('billing.selectOrganization')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid="org-picker"
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-gray-700 transition-colors hover:bg-surface-hover"
      >
        <span className="flex items-center gap-2 overflow-hidden">
          <FaBuilding className="h-4 w-4 shrink-0" />
          <span className={cn('truncate text-sm', !selected && 'text-text-placeholder')}>
            {selected?.organizationName ?? placeholder}
          </span>
        </span>
        <FaChevronDown
          className={cn('h-3 w-3 shrink-0 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={t('billing.selectOrganization')}
          onKeyDown={handleListKeyDown}
          className="absolute z-nav mt-1 max-h-64 w-full min-w-[12rem] overflow-auto rounded-lg border border-border bg-surface py-1 shadow-lg"
        >
          {memberships.map((m, i) => {
            const isActive = m.organizationId === selected?.organizationId
            return (
              <li
                key={m.organizationId}
                ref={(el) => {
                  optionRefs.current[i] = el
                }}
                role="option"
                aria-selected={isActive}
                tabIndex={-1}
                data-testid={`org-option-${m.organizationId}`}
                onClick={() => handleSelect(m)}
                className={cn(
                  'flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-border-strong',
                  isActive ? 'bg-primary-100 text-primary' : 'text-gray-700 hover:bg-surface-hover',
                )}
              >
                <span className="truncate">{m.organizationName}</span>
                {isActive && <FaCheck className="h-3 w-3 shrink-0" />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
